import { Link } from "@tanstack/react-router";

import { linkPropsWithSource } from "@/helpers/url";
import { cn } from "@/lib/utils";

import type { LocationType } from "../types";
import MapKit from "./map-kit";

const PROPERTY_CLASS_NAME = "text-white/80 text-12 md:text-15 leading-[1.2em]";

interface AllDetailsProps {
  className?: string;
  gameSlug?: string;
  location: LocationType;
}

export default function AllDetails({
  className,
  gameSlug,
  location,
}: AllDetailsProps) {
  const { address, address2, company, phoneNumber } = location;
  const urlProps = linkPropsWithSource(location.url || company.url, gameSlug);

  return (
    <div
      className={cn(
        "rounded-[15px] bg-deep-cove-dark md:rounded-[20px] md:bg-white/5 md:px-7.5 md:pt-7 md:pb-[37px]",
        className
      )}
    >
      <div className="flex flex-col items-stretch gap-2.5 p-5 pb-2.5 md:p-0 md:pb-0">
        <h2 className="font-medium text-16 text-white leading-[1.2em] md:text-24">
          <Link
            className="hover:underline"
            to={`/location/${company.slug}/${location.slug}`}
          >
            {location.displayName || company.name}
          </Link>
        </h2>
        <p className={PROPERTY_CLASS_NAME}>
          <a
            className="hover:underline"
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
              address.formatted_address
            )}`}
            rel="noopener noreferrer"
            target="_blank"
          >
            {address.formatted_address}
          </a>
          {!!address2 && (
            <>
              <br />
              {address2}
            </>
          )}
        </p>
        {!!phoneNumber && (
          <p className={PROPERTY_CLASS_NAME}>
            <a className="hover:underline" href={`tel:${phoneNumber}`}>
              {phoneNumber}
            </a>
          </p>
        )}
        {!!urlProps && (
          <p className={PROPERTY_CLASS_NAME}>
            <a
              className="hover:underline"
              rel="noopener noreferrer"
              target="_blank"
              {...urlProps}
            >
              Website
              <svg
                aria-hidden="true"
                className="ml-1 inline size-3.5 text-white/80"
                fill="none"
                viewBox="0 0 14 14"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.25 7.583v0c0 1.555 0 2.332-.281 2.935a3 3 0 01-1.451 1.45c-.603.282-1.38.282-2.935.282H6.55c-1.68 0-2.52 0-3.162-.327a3 3 0 01-1.311-1.311C1.75 9.97 1.75 9.13 1.75 7.45V6.417c0-1.555 0-2.332.281-2.935a3 3 0 011.451-1.45c.603-.282 1.38-.282 2.935-.282v0M8.57 5.43l3.68-3.68m0 0H9.033m3.217 0v3.217"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.3"
                />
              </svg>
            </a>
          </p>
        )}
      </div>
      <MapKit location={location.location.coordinates} />
    </div>
  );
}
